import type { CSSProperties, ReactNode } from 'react'

/**
 * Status badge primitive.
 *
 * Renders the canonical pill — uppercase mono label inside a rounded
 * tone-colored chip. No legacy class equivalent exists (crm/index.html
 * has .stage-badge / .score-pill / .tag-pill-sm for different roles, not
 * status), so Badge owns its entire visual contract. Every value is
 * token-driven.
 *
 * Variants (`status`, `score`, `stage`, `tag` from the canonical UX
 * governance) are deferred until each has a real consumer — this PR
 * ships only the `status` use case via the `tone` prop. When the next
 * variant's consumer surfaces, a `variant` prop is added additively.
 *
 * Tones map to the canonical status palette:
 *   ok    → green                (success, completion)
 *   warn  → amber                (soft warning, skip, not configured)
 *   err   → red                  (hard failure)
 *   muted → text-dim + border    (no data, n/a)
 */

type BadgeTone = 'ok' | 'warn' | 'err' | 'muted'

interface BadgeProps {
  tone?: BadgeTone
  children: ReactNode
}

const TONE_STYLES: Record<BadgeTone, CSSProperties> = {
  ok: { background: 'var(--green-glow)', color: 'var(--green)' },
  warn: { background: 'var(--amber-glow)', color: 'var(--amber)' },
  err: { background: 'var(--red-glow)', color: 'var(--red)' },
  muted: {
    background: 'transparent',
    color: 'var(--text-dim)',
    border: '1px solid var(--border2)',
  },
}

const BADGE_STYLE: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-xs)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontWeight: 500,
}

export function Badge({ tone = 'muted', children }: BadgeProps) {
  return <span style={{ ...BADGE_STYLE, ...TONE_STYLES[tone] }}>{children}</span>
}
